import { useCallback, useEffect, useMemo, useState } from "react";
import { api, ApiError } from "../api";
import type { EnvioLab, Row, Separacao } from "../types";
import ErrorState from "./ErrorState";
import PedirSeparacaoModal from "./PedirSeparacaoModal";
import EnviarLaboratorioModal from "./EnviarLaboratorioModal";
import { IconSearch, IconRefresh, IconTruck, IconFile } from "../icons";

interface Props {
  onDataChanged: () => void;
}

/**
 * Cor da pílula por status.
 *
 * Não reusa `statusTone` do format.ts de propósito: lá "Pendente" é VERMELHO,
 * porque nasceu para prazo vencido. Aqui pendente é o estado NORMAL de um
 * pedido recém-aberto — pintar de vermelho toda separação criada faria a tela
 * parecer um mural de problemas cinco segundos depois do primeiro pedido.
 */
const TOM: Record<string, string> = {
  Pendente: "warn",
  Aguardando: "warn",
  "Em separação": "warn",
  "Em análise": "warn",
  Recebida: "warn",
  Separado: "ok",
  Enviado: "ok",
  Atendida: "ok",
  Concluída: "ok",
  Despachada: "ok",
  Recusada: "danger",
  Cancelada: "neutral",
};

const tom = (status: string) => TOM[status] ?? "neutral";

/** "OS 1234" dentro do texto do pedido é como o Almoxarifado amarra os itens. */
function pedidoEhDaOs(pedido: string | null, os: string): boolean {
  if (!os) return false;
  return String(pedido ?? "").toUpperCase().includes(`OS ${os.toUpperCase()}`);
}

function fmtData(valor: string | null): string {
  if (!valor) return "—";
  const d = new Date(valor);
  return isNaN(d.getTime()) ? valor : d.toLocaleDateString("pt-BR");
}

/**
 * SEPARAÇÃO DE MATERIAL — a OS pedindo o que precisa para ser executada.
 *
 * Esta tela é o ponto em que o planejamento deixa de ser papel: a mesma OS que
 * foi planejada aqui pede os insumos ao Almoxarifado e avisa o Laboratório de
 * que o material vai chegar. Antes, os dois pedidos nasciam depois — o
 * almoxarife descobria a necessidade no dia da viagem e o laboratório descobria
 * a caixa quando ela já estava na bancada.
 *
 * As duas colunas de status são lidas dos OUTROS sistemas a cada carga, e não
 * copiadas para cá: o dono da informação é quem executa. Se um deles estiver
 * fora do ar, a linha continua aparecendo — só sem o andamento.
 */
export default function SeparacaoMaterialView({ onDataChanged }: Props) {
  const [atividades, setAtividades] = useState<Row[]>([]);
  const [separacoes, setSeparacoes] = useState<Separacao[]>([]);
  const [envios, setEnvios] = useState<EnvioLab[]>([]);
  const [almoxAviso, setAlmoxAviso] = useState<string | null>(null);
  const [labAviso, setLabAviso] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [busca, setBusca] = useState("");
  const [soComOs, setSoComOs] = useState(true);
  const [pedindo, setPedindo] = useState<Row | null>(null);
  const [enviando, setEnviando] = useState<Row | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // A lista de atividades é a espinha da tela — sem ela não há o que mostrar.
      const lista = await api.list("atividades");
      setAtividades(lista);
    } catch (err) {
      setError(err instanceof ApiError ? err : new ApiError(String(err)));
      setLoading(false);
      return;
    }

    // Almoxarifado e Laboratório são sistemas SEPARADOS: se um cair, a tela
    // continua útil (mostra a OS e deixa pedir de novo) — só perde o andamento.
    const [sep, env] = await Promise.allSettled([
      api.separacao.separacoes(),
      api.separacao.envios(),
    ]);

    if (sep.status === "fulfilled") {
      setSeparacoes(sep.value);
      setAlmoxAviso(null);
    } else {
      setSeparacoes([]);
      setAlmoxAviso(
        sep.reason instanceof ApiError
          ? sep.reason.message
          : "Não foi possível ler as separações do Almoxarifado.",
      );
    }

    if (env.status === "fulfilled") {
      setEnvios(env.value.envios);
      setLabAviso(
        env.value.labOffline
          ? "Os envios abaixo estão registrados, mas o Laboratório não respondeu agora — a coluna de andamento pode estar em branco."
          : null,
      );
    } else {
      setEnvios([]);
      setLabAviso(
        env.reason instanceof ApiError
          ? env.reason.message
          : "Não foi possível ler os envios ao Laboratório.",
      );
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  /** Envio ao Laboratório indexado pela OS (é a chave dos dois lados). */
  const envioPorOs = useMemo(() => {
    const m = new Map<string, EnvioLab>();
    for (const e of envios) m.set(String(e.os_numero ?? "").trim().toUpperCase(), e);
    return m;
  }, [envios]);

  const linhas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return atividades
      .filter((a) => (soComOs ? String(a.codigo_os ?? "").trim() !== "" : true))
      .filter((a) =>
        q
          ? [a.codigo_os, a.cliente, a.nome, a.colaborador_nome, a.cidade]
              .map((v) => String(v ?? "").toLowerCase())
              .some((v) => v.includes(q))
          : true,
      )
      .map((a) => {
        const os = String(a.codigo_os ?? "").trim();
        const daOs = separacoes.filter((s) => pedidoEhDaOs(s.pedido, os));
        return {
          atividade: a,
          os,
          separacoes: daOs,
          pendentes: daOs.filter((s) => s.status === "Pendente").length,
          envio: envioPorOs.get(os.toUpperCase()) ?? null,
        };
      });
  }, [atividades, separacoes, envioPorOs, busca, soComOs]);

  function recarregar() {
    load();
    onDataChanged();
  }

  if (error) return <ErrorState message={error.message} code={error.code} onRetry={load} />;

  return (
    <div className="crud">
      <div className="crud-toolbar">
        <div className="search-wrap">
          <IconSearch size={17} />
          <input
            className="search"
            placeholder="Buscar por OS, cliente, atividade ou responsável..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
        <div className="toolbar-right">
          <label className="sep-check sep-check-inline">
            <input
              type="checkbox"
              checked={soComOs}
              onChange={(e) => setSoComOs(e.target.checked)}
            />
            <span>Só atividades com OS</span>
          </label>
          <span className="badge">{linhas.length} atividade(s)</span>
          <button className="btn-primary" onClick={load} disabled={loading}>
            <IconRefresh size={17} /> {loading ? "Atualizando..." : "Atualizar"}
          </button>
        </div>
      </div>

      <div className="crud-aviso">
        O pedido de <strong>separação</strong> vai para a aba <strong>Solicitações</strong> do
        Almoxarifado; o <strong>envio ao laboratório</strong> cria a remessa em{" "}
        <strong>Material do cliente</strong> (e a análise, quando há ensaio). O andamento das duas
        colunas é lido desses sistemas na hora — aqui não fica cópia.
      </div>

      {almoxAviso && <div className="crud-aviso crud-aviso-erro">{almoxAviso}</div>}
      {labAviso && <div className="crud-aviso crud-aviso-erro">{labAviso}</div>}

      <div className="table-wrap">
        {loading ? (
          <div className="empty">Carregando...</div>
        ) : linhas.length === 0 ? (
          <div className="empty">
            Nenhuma atividade encontrada.
            {soComOs ? " Desmarque \"Só atividades com OS\" para ver as demais." : ""}
          </div>
        ) : (
          <table className="table table-compact">
            <thead>
              <tr>
                <th>OS</th>
                <th>Cliente</th>
                <th>Atividade</th>
                <th>Responsável</th>
                <th>Separação (Almoxarifado)</th>
                <th>Laboratório</th>
                <th className="col-actions">Ações</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map(({ atividade, os, separacoes: daOs, pendentes, envio }) => {
                const situacao = envio?.situacao ?? null;
                return (
                  <tr key={atividade.id}>
                    <td>{os || "—"}</td>
                    <td>{atividade.cliente ?? "—"}</td>
                    <td>{atividade.nome ?? "—"}</td>
                    <td>{atividade.colaborador_nome ?? "—"}</td>

                    <td>
                      {daOs.length === 0 ? (
                        <span className="muted">Nada pedido</span>
                      ) : (
                        <div className="sep-status">
                          <span className={`pill pill-${pendentes ? "warn" : "ok"}`}>
                            {pendentes
                              ? `${pendentes} pendente(s)`
                              : `${daOs.length} atendido(s)`}
                          </span>
                          <span className="muted">
                            {daOs.length} insumo(s) · {fmtData(daOs[0]?.data_solicitacao ?? null)}
                          </span>
                        </div>
                      )}
                    </td>

                    <td>
                      {!envio ? (
                        <span className="muted">Não enviado</span>
                      ) : (
                        <div className="sep-status">
                          <span className={`pill pill-${tom(situacao?.material_status ?? "")}`}>
                            {situacao?.material_status ?? "Registrado"}
                          </span>
                          <span className="muted">
                            {situacao
                              ? `${situacao.bipados}/${situacao.quantidade_prevista} conferido(s)`
                              : `${envio.quantidade_prevista} previsto(s)`}
                            {situacao?.analise_status ? ` · análise: ${situacao.analise_status}` : ""}
                          </span>
                        </div>
                      )}
                    </td>

                    <td className="col-actions">
                      {/* Os dois pedidos gravam (em outros sistemas, inclusive):
                          somem para quem é somente visualização — ver
                          `modo-leitura-esconde` em styles.css. */}
                      <button
                        className="btn-linha modo-leitura-esconde"
                        title="Pedir a separação dos insumos ao Almoxarifado"
                        onClick={() => setPedindo(atividade)}
                      >
                        <IconTruck size={15} /> Separação
                      </button>
                      <button
                        className="btn-linha modo-leitura-esconde"
                        title={
                          os
                            ? "Criar a remessa (e a análise) no Laboratório"
                            : "Preencha a OS na aba Atividades para enviar ao Laboratório"
                        }
                        disabled={!os}
                        onClick={() => setEnviando(atividade)}
                      >
                        <IconFile size={15} /> Laboratório
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {pedindo && (
        <PedirSeparacaoModal
          atividade={pedindo}
          onClose={() => setPedindo(null)}
          onPedida={recarregar}
        />
      )}
      {enviando && (
        <EnviarLaboratorioModal
          atividade={enviando}
          onClose={() => setEnviando(null)}
          onEnviado={recarregar}
        />
      )}
    </div>
  );
}
